import React, { ReactNode } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { HiOutlineDotsVertical } from 'react-icons/hi';

export interface DropdownAction {
  label: string;
  icon?: ReactNode;
  onClick: (e?: React.MouseEvent, closeDropdown?: () => void) => void;
  className?: string;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  isLoading?: boolean;
  keepOpenOnClick?: boolean;
}

interface ActionsDropdownProps {
  actions: DropdownAction[];
  triggerIcon?: ReactNode;
  triggerClassName?: string;
  dropdownClassName?: string;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  theme?: 'light' | 'dark';
  disabled?: boolean;
  size?: 'sm' | 'md';
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  actions,
  triggerIcon,
  triggerClassName = '',
  dropdownClassName = '',
  position = 'bottom-right',
  theme = 'dark',
  disabled = false,
  size = 'md',
}) => {
  const handleActionClick = (e: React.MouseEvent, action: DropdownAction, closeDropdown: () => void) => {
    if (action.isLoading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (action.keepOpenOnClick) {
      e.preventDefault();
    }

    action.onClick(e, closeDropdown);
  };

  const getTriggerClasses = () => {
    const sizeClasses = size === 'sm' ? 'p-1 rounded-md' : 'p-2 rounded-lg';
    let classes = `${sizeClasses} transition-all duration-200 ${triggerClassName} `;

    if (disabled) {
      classes += ' opacity-50 cursor-not-allowed';
    } else {
      if (theme === 'dark') {
        classes +=
          'bg-primary-intemediate border-2 border-primary-variant hover:bg-primary-subtle';
      } else {
        classes += ' bg-gray-100 hover:bg-gray-200';
      }
    }

    return classes;
  };

  const getDropdownClasses = () => {
    const sizeClasses = size === 'sm' ? 'min-w-[160px]' : 'min-w-[200px]';
    let classes = `${sizeClasses} rounded-lg shadow-lg border overflow-hidden ${dropdownClassName}`;

    if (theme === 'dark') {
      classes += ' bg-primary-intemediate border border-primary-variant';
    } else {
      classes += ' bg-white border-gray-200';
    }

    return classes;
  };

  const getActionClasses = (action: DropdownAction, focus: boolean) => {
    let classes =
      size === 'sm'
        ? 'w-full px-3 py-2 flex items-center gap-2 text-xs transition-colors duration-200 text-left'
        : 'w-full px-4 py-3 flex items-center gap-3 text-sm transition-colors duration-200 text-left';

    // Apply custom className if provided
    if (action.className) {
      classes += ` ${action.className}`;
    } else {
      // Apply variant-based styling
      switch (action.variant) {
        case 'danger':
          classes += theme === 'dark' ? ' text-red-300' : ' text-red-600';
          break;
        case 'warning':
          classes += theme === 'dark' ? ' text-yellow-300' : ' text-yellow-600';
          break;
        case 'success':
          classes += theme === 'dark' ? ' text-green-300' : ' text-green-600';
          break;
        default:
          classes += theme === 'dark' ? ' text-neutral' : ' text-gray-700';
      }

      // Add focus/hover states. Dark theme uses primary-subtle (#2A2F3D) —
      // primary-light (#131720) used to match the surrounding table/card
      // background almost exactly, making the hover state look like nothing
      // happened (QA-reported confusion on the Onboarding Queue actions menu).
      if (focus) {
        switch (action.variant) {
          case 'danger':
            classes += theme === 'dark' ? ' bg-primary-subtle' : ' bg-red-50';
            break;
          case 'warning':
            classes += theme === 'dark' ? ' bg-primary-subtle' : ' bg-yellow-50';
            break;
          case 'success':
            classes += theme === 'dark' ? ' bg-primary-subtle' : ' bg-green-50';
            break;
          default:
            classes += theme === 'dark' ? ' bg-primary-subtle' : ' bg-gray-50';
        }
      } else {
        // Add hover states for non-focused items
        switch (action.variant) {
          case 'danger':
            classes +=
              theme === 'dark' ? ' hover:bg-primary-subtle' : ' hover:bg-red-50';
            break;
          case 'warning':
            classes +=
              theme === 'dark'
                ? ' hover:bg-primary-subtle'
                : ' hover:bg-yellow-50';
            break;
          case 'success':
            classes +=
              theme === 'dark'
                ? ' hover:bg-primary-subtle'
                : ' hover:bg-green-50';
            break;
          default:
            classes +=
              theme === 'dark'
                ? ' hover:bg-primary-subtle'
                : ' hover:bg-gray-50';
        }
      }
    }

    return classes;
  };

  const getAnchorPosition = () => {
    switch (position) {
      case 'bottom-right':
        return 'bottom end';
      case 'bottom-left':
        return 'bottom start';
      case 'top-right':
        return 'top end';
      case 'top-left':
        return 'top start';
      default:
        return 'bottom end';
    }
  };

  return (
    <Menu as='div' className='relative inline-block text-left'>
      {({ close }) => (
        <>
          <MenuButton
            className={getTriggerClasses()}
            disabled={disabled}
            type='button'
          >
            {triggerIcon || (
              <HiOutlineDotsVertical className={size === 'sm' ? 'text-neutral-medium w-4 h-4' : 'text-neutral-medium w-5 h-5'} />
            )}
          </MenuButton>

          <MenuItems
            anchor={getAnchorPosition()}
            transition
            className={`z-[9999] transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 ${getDropdownClasses()}`}
          >
            {actions.map((action, index) => (
              <MenuItem key={index}>
                {({ focus }) => (
                  <button
                    onClick={(e) => handleActionClick(e, action, close)}
                    className={getActionClasses(action, focus)}
                    disabled={action.isLoading}
                  >
                    {action.isLoading ? (
                      <span className='flex-shrink-0 w-5 h-5 flex items-center justify-center'>
                        <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin border-gray-400"></div>
                      </span>
                    ) : action.icon ? (
                      <span className='flex-shrink-0 w-5 h-5 flex items-center justify-center'>
                        {action.icon}
                      </span>
                    ) : null}
                    <span className='flex-1'>{action.label}</span>
                  </button>
                )}
              </MenuItem>
            ))}
          </MenuItems>
        </>
      )}
    </Menu>
  );
};

export default ActionsDropdown;
